import { useEffect, useState } from 'react';
import {
  breakpoints,
  fontStyle,
  Modal,
  palette,
  Progress,
  spacing,
  Typography,
} from '@playdapp/ui';
import { useQuery } from 'react-query';
import { useMedia } from 'react-use';
import { useForm } from 'react-hook-form';
import styled from '@emotion/styled';
import { times } from 'lodash';
import { Input, Skeleton } from '@chakra-ui/react';

import type {
  SimplexPurchaseResponseData,
  SimplexQuoteResponseData,
} from 'types/simplex';

import { getSimplexPurchase, getSimplexQuote } from 'api/simplex';
import { getBreakpointQuery } from 'lib/util';
import { useAppSelector } from 'store/hooks';
import { chainIdToMainNetworkName } from 'lib/network';
import useOpenControl from 'hooks/useOpenControl';

import Button from '@/components/Button';
import SimplexConfirmModal from '@/components/Modal/SimplexConfirmModal';

type FormValue = {
  digital_currency: string;
  fiat_currency: string;
  requested_amount: number;
};

const CardModalBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const SkeletonBlock = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  width: 418px;
  height: 226px;
  margin-top: ${spacing.m};
  padding: ${spacing.l};
  background: ${palette.gray200};
  border-radius: 16px;
`;

const Title = styled.div`
  margin-bottom: ${spacing.xs};
  font-weight: 800;
  font-size: 24px;

  ${breakpoints.down('md')} {
    font-size: 20px;
  }
`;

const ContentBlock = styled.div`
  margin-top: ${spacing.m};

  form {
    display: flex;
    flex-direction: column;
    align-items: center;
  }
`;

const InputWrapper = styled.div`
  margin-bottom: ${spacing.l};
  padding: ${spacing.l};
  background: ${palette.gray200};
  border-radius: 16px;
`;

const InputSection = styled.div`
  margin-bottom: ${spacing.m};
`;

const InputBlock = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 376px;
  margin: ${spacing.xs} 0 ${spacing.xxs} 0;
  padding: ${spacing.s};
  background: ${palette.white};
  border: 1px solid ${palette.gray600};
  border-radius: 8px;

  input {
    width: 70%;
    height: 90%;
    ${fontStyle('p3')};
  }

  ${breakpoints.down('md')} {
    max-width: 262px;
    min-width: 230px;
    margin: 0 auto;

    input {
      ${fontStyle('p4')};
    }
  }
`;

const DigitalInputBlock = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  width: 376px;
  margin: ${spacing.xs} 0 ${spacing.xxs} 0;
  padding: ${spacing.s};
  background: ${palette.white};
  border: 1px solid ${palette.gray600};
  border-radius: 8px;

  ${breakpoints.down('md')} {
    max-width: 262px;
    min-width: 230px;
    margin: 0 auto;
  }
`;

const FiatCurrencySelect = styled.select`
  width: 20%;
  text-align: center;
  ${fontStyle('b4')};

  ${breakpoints.down('md')} {
    ${fontStyle('b5')};
  }
`;

const DigitalCurrencySelect = styled.select`
  ${fontStyle('b4')};

  ${breakpoints.down('md')} {
    ${fontStyle('b5')};
  }
`;

const DigitalCurrencyBlock = styled.div`
  width: 100%;
  text-align: right;
  ${fontStyle('b4')};

  ${breakpoints.down('md')} {
    ${fontStyle('b5')};
  }
`;

const DigitalCurrencyErrorText = styled.span`
  ${fontStyle('p4')};
  color: ${palette.red};
`;

const ErrorBlock = styled.div`
  margin-top: ${spacing.xxs};
  ${fontStyle('p5')};
  color: ${palette.red500};
`;

const useSimplex = () => {
  const isMdDown = useMedia(getBreakpointQuery(breakpoints.down('md')), false);
  const [isOpen, setIsOpen] = useState(false);
  const [isConfirmModalOpen, handleConfirmModalOpen] = useOpenControl();
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoadingSubmit, setLoadingSubmit] = useState(false);
  const [orderData, setOrderData] = useState<SimplexQuoteResponseData | null>(
    null,
  );
  const [confirmData, setConfirmData] =
    useState<SimplexPurchaseResponseData | null>(null);

  const { account, chainId } = useAppSelector(({ wallet }) => wallet);
  const network = chainIdToMainNetworkName[chainId || 137];

  const { isLoading, data: quoteData } = useQuery(
    'getSimplexQuote',
    async () => {
      if (account) {
        const { data: quoteData } = await getSimplexQuote({
          end_user_id: account,
          network,
        });

        if (quoteData) {
          setValue(
            'digital_currency',
            quoteData?.supported_digital_currencies[0],
          );
          setValue('fiat_currency', quoteData?.supported_fiat_currencies[0]);
        }

        return quoteData;
      }
    },
    { enabled: !!account, refetchOnWindowFocus: false },
  );

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    reset,
    formState: { errors },
  } = useForm<FormValue>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
  });

  const handleOpen = (isModalOpen: boolean) => {
    setIsOpen(isModalOpen);
  };

  const handleClickSubmit = async () => {
    if (account) {
      const digitalCurrency =
        getValues('digital_currency') ??
        quoteData?.supported_digital_currencies[0];

      try {
        setLoadingSubmit(true);

        const { data: submitData, message } = await getSimplexQuote({
          end_user_id: account,
          network,
          digital_currency: digitalCurrency,
          fiat_currency: getValues('fiat_currency'),
          requested_currency: getValues('fiat_currency'),
          requested_amount: getValues('requested_amount'),
        });

        if (message) {
          setErrorMessage(message);
        }

        if (submitData) {
          const { data: purchaseData } = await getSimplexPurchase({
            app_end_user_id: account,
            quote_id: submitData?.quote_id,
            currency: digitalCurrency,
            address: account,
            network,
          });

          setOrderData(submitData);

          if (purchaseData) {
            setConfirmData(purchaseData);
            handleConfirmModalOpen(true);
          }
        }

        return submitData;
      } catch (e) {
        setLoadingSubmit(false);
      } finally {
        setLoadingSubmit(false);
      }
    }
  };

  useEffect(() => {
    reset();
    setErrorMessage('Success');
    setLoadingSubmit(false);
  }, [isOpen]);

  return {
    isOpen,
    handleOpen,
    SimplexModal: () => (
      <>
        <Modal
          isOpen={isOpen}
          handleOpen={handleOpen}
          width={{ lg: '498px', xl: '498px' }}
        >
          <CardModalBlock>
            {isLoading && (
              <>
                <Skeleton width="100%" height={57} borderRadius={8} />
                <SkeletonBlock>
                  {times(2, (index) => (
                    <Skeleton
                      key={index}
                      width="100%"
                      height={78}
                      borderRadius={8}
                    />
                  ))}
                </SkeletonBlock>
              </>
            )}
            {!isLoading && (
              <>
                <Title>Buy with Simplex</Title>
                <Typography type={isMdDown ? `p4` : `p3`} color="gray900">
                  Enter the amount of fiat money
                </Typography>
                <ContentBlock>
                  <form onSubmit={handleSubmit(handleClickSubmit)}>
                    <InputWrapper>
                      <InputSection>
                        <Typography type={isMdDown ? `b4` : `b3`}>
                          Fiat Currency
                        </Typography>
                        <InputBlock>
                          <Input
                            variant="unstyled"
                            type="number"
                            placeholder="Please type number."
                            {...register('requested_amount', {
                              required: 'Number is required.',
                            })}
                          />
                          {quoteData && (
                            <FiatCurrencySelect {...register('fiat_currency')}>
                              {quoteData.supported_fiat_currencies.map(
                                (fiatCurrency) => (
                                  <option
                                    value={fiatCurrency}
                                    key={fiatCurrency}
                                  >
                                    {fiatCurrency}
                                  </option>
                                ),
                              )}
                            </FiatCurrencySelect>
                          )}
                        </InputBlock>
                        {errors.requested_amount?.message && (
                          <ErrorBlock>
                            {errors.requested_amount?.message}
                          </ErrorBlock>
                        )}
                        {errorMessage !== 'Success' && (
                          <ErrorBlock>{errorMessage}</ErrorBlock>
                        )}
                      </InputSection>
                      <InputSection>
                        <Typography type={isMdDown ? `b4` : `b3`}>
                          Digital Currency
                        </Typography>
                        <DigitalInputBlock>
                          {quoteData &&
                          quoteData?.supported_digital_currencies.length > 1 ? (
                            <DigitalCurrencySelect
                              {...register('digital_currency')}
                            >
                              {quoteData?.supported_digital_currencies.map(
                                (digitalCurrency) => (
                                  <option
                                    value={digitalCurrency}
                                    key={digitalCurrency}
                                  >
                                    {digitalCurrency}
                                  </option>
                                ),
                              )}
                            </DigitalCurrencySelect>
                          ) : (
                            <DigitalCurrencyBlock>
                              {quoteData ? (
                                quoteData.supported_digital_currencies[0]
                              ) : (
                                <DigitalCurrencyErrorText>
                                  An unexpected error occurred. please try
                                  again.
                                </DigitalCurrencyErrorText>
                              )}
                            </DigitalCurrencyBlock>
                          )}
                        </DigitalInputBlock>
                      </InputSection>
                    </InputWrapper>
                    {isLoadingSubmit ? (
                      <Progress size="large" />
                    ) : (
                      <Button type="submit" isDisabled={!quoteData}>
                        Confirm your order
                      </Button>
                    )}
                  </form>
                </ContentBlock>
              </>
            )}
          </CardModalBlock>
        </Modal>

        {orderData && confirmData && (
          <SimplexConfirmModal
            isOpen={isConfirmModalOpen}
            handleOpen={handleConfirmModalOpen}
            fiatAmount={orderData.fiat_money.total_amount}
            fiatCurrency={orderData?.fiat_money.currency}
            digitalAmount={orderData.digital_money.amount}
            digitalCurrency={orderData.digital_money.currency}
            confirmData={confirmData}
          />
        )}
      </>
    ),
  };
};

export default useSimplex;
